import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { getLanguage } from 'react-switch-lang';
import en from '../../en.json';
import ma from '../../ma.json';
import EventDetailsModal from './EventDetailsModal';

import { toast } from 'react-toastify';
import './CalendarView.css';
import { Link } from 'react-router-dom';
import * as myConstClass from '../../constant.js';

import { buildDeepLink, shareDeepLink } from '../../utils/deepLink';

/**
 * Calendar View Component
 * Displays events in a monthly calendar grid formatnn
 * Week starts on Monday and ends on Sunday
 */
const CalendarView = ({ events, currentMonth, currentYear, onMonthChange }) => {
    const language = getLanguage();
    const isMarathi = language === 'ma';
    
    // Single event details modal state
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);

    // Eligibility info popup state
    const [eligibilityPopup, setEligibilityPopup] = useState({
        show: false,
        event: null,
        position: { top: 0, left: 0 },
        anchor: 'center',
        arrow: '50%'
    });
    //info popup state
    const [infoPopup, setInfoPopup] = useState({
        show: false,
        event: null,
        position: { top: 0, left: 0 },
        anchor: 'center',
        arrow: '50%'
    });

    const locationHoverTimeoutRef = useRef(null);

    // Cache for fetched event details (for info popup)
    const [eventDetailsCache, setEventDetailsCache] = useState({});
    const [infoLoading, setInfoLoading] = useState(false);
    // Mobile modal for touch devices (location / info / eligibility)
    const [mobileModal, setMobileModal] = useState({ show: false, type: '', event: null });

    const isMobileView = () => {
        try {
            return window.innerWidth < 768;
        } catch (e) {
            return false;
        }
    };

    const openMobileModal = async (type, event) => {
        if (!event) return;
        if (type === 'info') {
            await fetchEventDetailsForInfo(event.eventId);
        }
        setMobileModal({ show: true, type, event });
    };

    const closeMobileModal = () => setMobileModal({ show: false, type: '', event: null });

    // "More events" modal state (when user clicks on "+x more")
    const [moreEventsDate, setMoreEventsDate] = useState(null);
    const [moreEventsList, setMoreEventsList] = useState([]);
    const [isMoreEventsModalOpen, setIsMoreEventsModalOpen] = useState(false);
    
    // Refs for measuring cell dimensions
    const calendarDaysRef = useRef(null);
    const overlaysRef = useRef({}); // map eventId -> DOM element for multi-day overlays
    const [cellHeight, setCellHeight] = useState(100); // Default desktop height
    const [dayNumberHeight, setDayNumberHeight] = useState(25); // Default day number height
    const [activeOverlayId, setActiveOverlayId] = useState(null); // bring overlay to top on interaction
    
    const handleEventClick = (event, e, startDateOverride) => {
        e.preventDefault();
        e.stopPropagation();
        // Create a shallow copy and prefer an explicit start date override
        const evtCopy = { ...event };
        if (startDateOverride) {
            // Use calendar-provided original start date fields so EventDetailsModal
            // prioritizes the occurrence passed from the calendar view.
            evtCopy.startDateOriginal = startDateOverride;
            // Also set startDate for consumers that expect it
            evtCopy.startDate = startDateOverride;
            
            // Calculate and set the end date based on event duration
            const duration = event.duration || 1;
            if (duration > 1) {
                // Multi-day event: calculate end date from start date + duration
                const endDate = new Date(startDateOverride);
                endDate.setDate(endDate.getDate() + duration - 1);
                
                // Format as YYYY-MM-DD for endDateOriginal (matches API format)
                const endDateStr = `${endDate.getFullYear()}-${String(endDate.getMonth() + 1).padStart(2, '0')}-${String(endDate.getDate()).padStart(2, '0')}`;
                
                evtCopy.endDateOriginal = endDateStr;
                evtCopy.endDate = endDate;
            } else {
                // Single-day event: end date is same as start date
                const startDateStr = `${startDateOverride.getFullYear()}-${String(startDateOverride.getMonth() + 1).padStart(2, '0')}-${String(startDateOverride.getDate()).padStart(2, '0')}`;
                evtCopy.endDateOriginal = startDateStr;
                evtCopy.endDate = startDateOverride;
            }
        }
        setSelectedEvent(evtCopy);
        setIsModalOpen(true);
    };

    const handleLocationHoverIn = (event, e, startDateOverride) => {
        e.preventDefault();
        e.stopPropagation();

        // cancel pending close
        if (locationHoverTimeoutRef.current) {
            clearTimeout(locationHoverTimeoutRef.current);
            locationHoverTimeoutRef.current = null;
        }

        const evtCopy = { ...event };

        if (startDateOverride) {
            evtCopy.startDateOriginal = startDateOverride;
            evtCopy.startDate = startDateOverride;
        }

        setSelectedEvent(evtCopy);
        setIsModalOpen(true);
    };


    // const handleEligibilityInfoClick = (e, event) => {
    //     e.preventDefault();
    //     e.stopPropagation();

    //       //Close info popup if open
    //         setInfoPopup({
    //             show: false,
    //             event: null,
    //             position: { top: 0, left: 0 }
    //         });


    //     const rect = e.currentTarget.getBoundingClientRect();
    //     const POPUP_WIDTH = 220;
    //     const GAP = 8;
    //     const viewportWidth = window.innerWidth;

    //     let left = rect.left;
    //     if (rect.left + POPUP_WIDTH > viewportWidth) {
    //         left = rect.right - POPUP_WIDTH;
    //     }
    //     console.log("left", left);
    //     const top = rect.bottom + GAP;

    //     setEligibilityPopup(prev => {
    //         if (
    //             prev.show &&
    //             prev.event?.eventId === event.eventId
    //         ) {
    //             return {
    //                 show: false,
    //                 event: null,
    //                 position: { top: 0, left: 0 }
    //             };
    //         }

    //         return {
    //             show: true,
    //             event: event, // ✅ store event here
    //             position: { top, left }
    //         };
    //     });
    // };
    // fetch event description in i icon popup

    const fetchEventDetailsForInfo = async (eventId) => {
        if (!eventId || eventDetailsCache[eventId]) return;

        setInfoLoading(true);

        try {
            const userId = localStorage.getItem('user_id') || 0;
            const apiUrl = `${myConstClass.BASE_URL}event/getEventInfoFrontEnd/${eventId}/${language}/0/${userId}`;

            const response = await fetch(apiUrl);
            if (!response.ok) throw new Error('Failed to fetch');

            const data = await response.json();
            const eventData = data.data || data;

            setEventDetailsCache(prev => ({
                ...prev,
                [eventId]: eventData
            }));
        } catch (err) {
            console.error('Info popup fetch failed:', err);
        } finally {
            setInfoLoading(false);
        }
    };


//     const handleInfoIconClick = (e, event) => {
//     e.preventDefault();
//     e.stopPropagation();

//     // Close eligibility popup if open
//     setEligibilityPopup({
//         show: false,
//         event: null,
//         position: { top: 0, left: 0 }
//     });

//     fetchEventDetailsForInfo(event.eventId);

//     const rect = e.currentTarget.getBoundingClientRect();
//     const POPUP_WIDTH = 220;
//     const GAP = 8;
//     const viewportWidth = window.innerWidth;

//     let left = rect.left;

//     // If popup goes out of viewport on right, shift left
//     if (rect.left + POPUP_WIDTH > viewportWidth) {
//         left = rect.right - POPUP_WIDTH;
//     }
//     console.log("left", left);
//     const top = rect.bottom + GAP;

//     setInfoPopup(prev => {
//         if (prev.show && prev.event?.eventId === event.eventId) {
//             return {
//                 show: false,
//                 event: null,
//                 position: { top: 0, left: 0 }
//             };
//         }

//         return {
//             show: true,
//             event,
//             position: { top, left }
//         };
//     });
// };

    const calculatePopupPosition = (targetEl, popupWidth = 220, popupHeight = 140) => {
        const rect = targetEl.getBoundingClientRect();
        // Use different gaps for below/above placement so popup sits closer
        // to the icon when placed above (reduce perceived vertical distance).
        const GAP_BELOW = 8;
        // Instead of only reducing the gap, allow the popup to overlap down
        // slightly when placed above the icon so the arrow visually touches it.
        const ARROW_OVERLAP = 10; // px - move popup down by this amount when above
        let GAP = GAP_BELOW;

        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;

        // ALWAYS anchor to ICON CENTER
        const iconCenterX = rect.left + rect.width / 2;

        let top = rect.bottom + GAP;
        let left = iconCenterX - popupWidth / 2;

        let anchor = 'center';
        // placement: 'below' (popup appears below icon) or 'above' (popup appears above icon)
        let placement = 'below';

        // Clamp horizontally with padding
        const PAD = 10;
        if (left + popupWidth > viewportWidth - PAD) {
            left = Math.max(PAD, viewportWidth - popupWidth - PAD);
            anchor = 'right';
        }

        if (left < PAD) {
            left = PAD;
            anchor = 'left';
        }

        // Flip vertically if needed (show above the icon)
        if (top + popupHeight > viewportHeight) {
            // place above the icon and move it down slightly so the arrow
            // appears to touch the icon (reduces visual gap)
            top = rect.top - popupHeight + ARROW_OVERLAP;
            placement = 'above';
            GAP = 0;
        }

        // Compute arrow position inside popup as percentage so the CSS arrow
        // can be placed exactly under the icon center even when popup is shifted.
        const effectivePopupLeft = left;
        const arrowOffsetPx = iconCenterX - effectivePopupLeft;
        // clamp arrowOffsetPx within popup bounds
        const minPx = 12; // avoid edges
        const maxPx = popupWidth - 12;
        const clampedPx = Math.max(minPx, Math.min(maxPx, arrowOffsetPx));
        const arrowPercent = `${(clampedPx / popupWidth) * 100}%`;

        return { top, left, anchor, arrow: arrowPercent, placement };
    };



    // Hover IN – Eligibility
    const handleEligibilityHover = (e, event) => {
        e.preventDefault();
        e.stopPropagation();

        setInfoPopup({
            show: false,
            event: null,
            position: { top: 0, left: 0 },
            anchor: 'center',
            arrow: '50%'
        });

        const { top, left, anchor, arrow, placement } = calculatePopupPosition(e.currentTarget);

        setEligibilityPopup({
            show: true,
            event,
            position: { top, left },
            anchor,
            arrow,
            placement
        });
    };


    // Hover OUT – Eligibility
    const handleEligibilityLeave = () => {
        setEligibilityPopup({
            show: false,
            event: null,
            position: { top: 0, left: 0 },
            anchor: 'center',
            arrow: '50%'
        });
    };

    // Hover IN – Info
    const handleInfoHover = (e, event) => {
        e.preventDefault();
        e.stopPropagation();

        setEligibilityPopup({
            show: false,
            event: null,
            position: { top: 0, left: 0 },
            anchor: 'center',
            arrow: '50%'
        });

        fetchEventDetailsForInfo(event.eventId);

        const { top, left, anchor, arrow, placement } = calculatePopupPosition(e.currentTarget);

        setInfoPopup({
            show: true,
            event,
            position: { top, left },
            anchor,
            arrow,
            placement
        });
    };

    // Hover OUT – Info
    const handleInfoLeave = () => {
        setInfoPopup({
            show: false,
            event: null,
            position: { top: 0, left: 0 },
            anchor: 'center',
            arrow: '50%'
        });
    };


    const getFirstLineOfDescription = (event) => {
        if (!event) return '';

        // Prefer short description if available
        const raw =
            event.shortDescription ||
            event.description ||
            event.eventDescription ||
            '';

        if (!raw) return '';

        // Strip HTML tags
        const text = raw.replace(/<[^>]*>/g, '').trim();

        // Split by new line or sentence end
        const parts = text
            .split(/\r?\n|\./)
            .map(p => p.trim())
            .filter(Boolean);

        // Take first 2–3 lines
        const preview = parts.slice(0, 3).join('. ');

        return parts.length > 3 ? preview + '…' : preview;
    };

    // Share / Deep-link handler for calendar events
    const handleShare = async (event, e) => {
        if (e && e.stopPropagation) e.stopPropagation();

        if (!event || (!event.eventId && !event.scheduleId)) {
            toast.error('Unable to share: Event data missing');
            return;
        }

        try {
            const origin = (typeof window !== 'undefined' && window.location && window.location.origin) ? window.location.origin : '';
            const eventId = event.eventId || event.event_id || event.id;
            const scheduleId = event.scheduleId || event.schedule_id;
            let url = `${origin}/event_details?event_id=${eventId}`;
            if (scheduleId) {
                url += `&schedule_id=${scheduleId}`;
            }
            // Category: Prefer event.categoryLabel, fallback to event.topicName, fallback to 'Event'
            const category = event.categoryLabel || event.topicName || 'Event';
            // Date range: Prefer event.dateRange, fallback to event.startDateOriginal, fallback to event.startDate or event.date
            const dateRange = event.dateRange || event.startDateOriginal || event.startDate || event.date || '';
            // Location
            const location = event.location || event.venueAddress || '';
            // Description: up to 100 chars, plain text
            let shortDesc = '';
            if (event.description) {
                const stripped = String(event.description).replace(/<[^>]*>/g, '');
                const normalized = stripped.replace(/\s+/g, ' ').trim();
                if (normalized.length <= 100) {
                    shortDesc = normalized;
                } else {
                    const lastSpace = normalized.lastIndexOf(' ', 100);
                    if (lastSpace > 0) {
                        shortDesc = normalized.slice(0, lastSpace).trim() + '...';
                    } else {
                        shortDesc = normalized.slice(0, 100).trim() + '...';
                    }
                }
            }

            // Compose the share text (do NOT include the URL here for native share)
            const shareText = `Book the ${category}: ${event.name || ''}${dateRange ? ` – ${dateRange}` : ''}${location ? ` at ${location}` : ''}\n${shortDesc}`;

            if (typeof navigator !== 'undefined' && navigator.share) {
                await navigator.share({
                    title: event.name || 'Event',
                    text: shareText,
                    url: url
                });
                toast.success('Link copied/shared');
                return true;
            }
            if (typeof navigator !== 'undefined' && navigator.clipboard && navigator.clipboard.writeText) {
                await navigator.clipboard.writeText(`${shareText}\n${url}`);
                toast.success('Link copied to clipboard!');
                return true;
            }
            window.prompt('Copy this link:', `${shareText}\n${url}`);
            return true;
        } catch (err) {
            console.error('Share failed', err);
            toast.error('Failed to share link');
            return false;
        }
    };

    /**
     * Deduplicate events list so that multi-day events or repeated items
     * with the same identifiers are only counted once.
     */
    const deduplicateEvents = (eventsForDate) => {
        const uniqueEvents = [];
        const seenEventIds = new Set();
        
        eventsForDate.forEach(event => {
            // Prefer scheduleId (unique per schedule) for deduplication,
            // fall back to eventId or generated key when not available.
            const eventKeyId = event.scheduleId || event.eventId || event.id;

            // If we have a key id, use it for deduplication
            if (eventKeyId !== undefined && eventKeyId !== null) {
                if (!seenEventIds.has(eventKeyId)) {
                    seenEventIds.add(eventKeyId);
                    uniqueEvents.push(event);
                }
            } else {
                // Fallback: use combination of name, startDate, and location as unique identifier
                const eventStartDate = getEventStartDate(event);
                const uniqueKey = `${event.name || ''}_${eventStartDate ? eventStartDate.getTime() : ''}_${event.location || event.cityName || ''}`;
                
                if (!seenEventIds.has(uniqueKey)) {
                    seenEventIds.add(uniqueKey);
                    uniqueEvents.push(event);
                }
            }
        });

        return uniqueEvents;
    };
    
    const handleMoreEventsClick = (date, eventsForDate, e) => {
        e.preventDefault();
        e.stopPropagation();
        
        // Use the same de-duplicated list that we use for counting
        const uniqueEvents = deduplicateEvents(eventsForDate);
        
        setMoreEventsDate(date);
        setMoreEventsList(uniqueEvents);
        setIsMoreEventsModalOpen(true);
    };
    
    const handleCloseModal = () => {
        setIsModalOpen(false);
        setSelectedEvent(null);
    };
    
    const handleCloseMoreEventsModal = () => {
        setIsMoreEventsModalOpen(false);
        setMoreEventsDate(null);
        setMoreEventsList([]);
    };
    
    // Measure cell dimensions for responsive positioning
    useEffect(() => {
        const measureCellDimensions = () => {
            if (calendarDaysRef.current) {
                // Get all current month cells
                const cells = calendarDaysRef.current.querySelectorAll('.calendar-day:not(.calendar-day-other-month)');
                
                if (cells.length > 0) {
                    // Use the first current month cell to measure
                    const firstCell = cells[0];
                    const cellRect = firstCell.getBoundingClientRect();
                    const dayNumberEl = firstCell.querySelector('.calendar-day-number');
                    
                    // Only update if we got a valid measurement
                    if (cellRect.height > 0) {
                        setCellHeight(cellRect.height);
                    }
                    
                    if (dayNumberEl) {
                        const dayNumberRect = dayNumberEl.getBoundingClientRect();
                        if (dayNumberRect.height > 0) {
                            setDayNumberHeight(dayNumberRect.height);
                        }
                    }
                }
            }
        };
        
        // Measure after a short delay to ensure DOM is fully rendered
        const timeoutId1 = setTimeout(measureCellDimensions, 50);
        const timeoutId2 = setTimeout(measureCellDimensions, 200);
        
        // Measure on window resize with debounce
        let resizeTimeout;
        const handleResize = () => {
            clearTimeout(resizeTimeout);
            resizeTimeout = setTimeout(measureCellDimensions, 100);
        };
        
        window.addEventListener('resize', handleResize);
        
        return () => {
            window.removeEventListener('resize', handleResize);
            clearTimeout(timeoutId1);
            clearTimeout(timeoutId2);
            clearTimeout(resizeTimeout);
        };
    }, [currentMonth, currentYear]);

    useEffect(() => {
    const handleScroll = () => {
        setInfoPopup({
            show: false,
            event: null,
            position: { top: 0, left: 0 }
        });
        setEligibilityPopup({
            show: false,
            event: null,
            position: { top: 0, left: 0 }
        });
    };

    window.addEventListener('scroll', handleScroll, true);

    return () => {
        window.removeEventListener('scroll', handleScroll, true);
    };
}, []);

    
    // Get month and year from currentMonth or use current date
    const getMonthYear = () => {
        if (currentMonth !== undefined && currentYear !== undefined) {
            return { month: currentMonth, year: currentYear };
        }
        const now = new Date();
        return { month: now.getMonth(), year: now.getFullYear() };
    };
    
    const { month, year } = getMonthYear();
    
    // Month names
    const monthNames = isMarathi
        ? ['जानेवारी', 'फेब्रुवारी', 'मार्च', 'एप्रिल', 'मे', 'जून', 'जुलै', 'ऑगस्ट', 'सप्टेंबर', 'ऑक्टोबर', 'नोव्हेंबर', 'डिसेंबर']
        : ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    
    // Day names (starting from Monday - European style)
    const dayNames = isMarathi
        ? ['सोम', 'मंगळ', 'बुध', 'गुरु', 'शुक्र', 'शनि', 'रवि']
        : ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    
    // Get first day of month (0 = Sunday, 1 = Monday, etc. in JavaScript Date)
    const firstDayOfMonth = new Date(year, month, 1).getDay();
    // Convert to Monday-based: Monday = 0, Tuesday = 1, ..., Sunday = 6
    const firstDay = (firstDayOfMonth + 6) % 7;
    
    // Get number of days in month
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    
    // Get previous month's trailing days
    const prevMonthDays = new Date(year, month, 0).getDate();
    
    // Build calendar grid
    const calendarDays = [];
    
    // Add previous month's trailing days (if week doesn't start on Monday)
    for (let i = firstDay - 1; i >= 0; i--) {
        calendarDays.push({
            day: prevMonthDays - i,
            isCurrentMonth: false,
            date: new Date(year, month - 1, prevMonthDays - i)
        });
    }
    
    // Add current month's days
    for (let day = 1; day <= daysInMonth; day++) {
        calendarDays.push({
            day,
            isCurrentMonth: true,
            date: new Date(year, month, day)
        });
    }
    
    // Add next month's leading days to complete the grid (6 weeks = 42 days)
    const totalDays = calendarDays.length;
    const remainingDays = 42 - totalDays;
    for (let day = 1; day <= remainingDays; day++) {
        calendarDays.push({
            day,
            isCurrentMonth: false,
            date: new Date(year, month + 1, day)
        });
    }

    // Maximum visible items per day (single-day cards + multi-day overlays)
    const MAX_VISIBLE_EVENTS_PER_DAY = 2; // show up to 2 items per date box (including overlays)

    
    // Get event start date from various possible formats
    const getEventStartDate = (event) => {
        // Priority 1: Use startDate if it's a Date object
        if (event.startDate instanceof Date && !isNaN(event.startDate.getTime())) {
            return event.startDate;
        }
        
        // Priority 2: Parse startDate if it's a string
        if (event.startDate) {
            try {
                const parsed = new Date(event.startDate);
                if (!isNaN(parsed.getTime())) {
                    return parsed;
                }
            } catch (e) {
                // Ignore
            }
        }
        
        // Priority 3: Parse dateKey (YYYY-MM-DD format)
        if (event.dateKey && event.dateKey.includes('-')) {
            try {
                const [y, m, d] = event.dateKey.split('-');
                if (y && m && d) {
                    const parsed = new Date(parseInt(y), parseInt(m) - 1, parseInt(d));
                    if (!isNaN(parsed.getTime())) {
                        return parsed;
                    }
                }
            } catch (e) {
                // Ignore
            }
        }
        
        // Priority 4: Parse date string
        if (event.date) {
            try {
                const dateMatch = event.date.match(/(\d+)\s+(\w+)/);
                if (dateMatch) {
                    const day = parseInt(dateMatch[1]);
                    const monthName = dateMatch[2];
                    const monthIndex = monthNames.findIndex(m => 
                        m.toLowerCase().includes(monthName.toLowerCase()) ||
                        monthName.toLowerCase().includes(m.toLowerCase())
                    );
                    if (monthIndex !== -1) {
                        const parsed = new Date(year, monthIndex, day);
                        if (!isNaN(parsed.getTime())) {
                            return parsed;
                        }
                    }
                }
            } catch (e) {
                // Ignore
            }
        }
        
        return null;
    };
    
    // Group events by date key (YYYY-MM-DD)
    const eventsByDate = {};
    
    events.forEach(event => {
        const eventStartDate = getEventStartDate(event);
        if (eventStartDate) {
            const dateKey = `${eventStartDate.getFullYear()}-${String(eventStartDate.getMonth() + 1).padStart(2, '0')}-${String(eventStartDate.getDate()).padStart(2, '0')}`;
            if (!eventsByDate[dateKey]) {
                eventsByDate[dateKey] = [];
            }
            eventsByDate[dateKey].push(event);
        }
    });
    
    // Get events for a specific date
    const getEventsForDate = (date) => {
        const dateKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
        return eventsByDate[dateKey] || [];
    };
    
    // Check if an event should be displayed on a specific date
    const shouldDisplayEventOnDate = (event, date) => {
        const eventStartDate = getEventStartDate(event);
        if (!eventStartDate) return false;
        
        // Normalize dates to compare only year, month, day (ignore time)
        const eventDateOnly = new Date(eventStartDate.getFullYear(), eventStartDate.getMonth(), eventStartDate.getDate());
        const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());
        
        // For single-day events, only show on start date
        const duration = event.duration || 1;
        if (duration === 1) {
            return eventDateOnly.getTime() === dateOnly.getTime();
        }
        
        // For multi-day events, check if this date is within the event duration
        const eventEndDate = new Date(eventStartDate);
        eventEndDate.setDate(eventEndDate.getDate() + duration - 1);
        const eventEndDateOnly = new Date(eventEndDate.getFullYear(), eventEndDate.getMonth(), eventEndDate.getDate());
        
        return dateOnly >= eventDateOnly && dateOnly <= eventEndDateOnly;
    };
    
    // Check if event starts on this date (for multi-day events)
    const isEventStartDate = (event, date) => {
        const eventStartDate = getEventStartDate(event);
        if (!eventStartDate) return false;
        
        return eventStartDate.toDateString() === date.toDateString();
    };
    
    // Get event end date
    const getEventEndDate = (event) => {
        const eventStartDate = getEventStartDate(event);
        if (!eventStartDate) return null;
        
        const duration = event.duration || 1;
        const eventEndDate = new Date(eventStartDate);
        eventEndDate.setDate(eventEndDate.getDate() + duration - 1);
        
        return eventEndDate;
    };
    
    // Calculate how many days an event should span from a given start date
    // This ensures the event only spans until its actual end date, not beyond
    const calculateEventSpan = (event, startDate, dayOfWeek) => {
        const eventStartDate = getEventStartDate(event);
        if (!eventStartDate) return 0;
        
        const eventEndDate = getEventEndDate(event);
        if (!eventEndDate) return 0;
        
        // Normalize dates to compare only year, month, day
        const startDateOnly = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
        const eventStartDateOnly = new Date(eventStartDate.getFullYear(), eventStartDate.getMonth(), eventStartDate.getDate());
        const eventEndDateOnly = new Date(eventEndDate.getFullYear(), eventEndDate.getMonth(), eventEndDate.getDate());
        
        // If the start date doesn't match the event start date, don't span
        if (startDateOnly.getTime() !== eventStartDateOnly.getTime()) {
            return 0;
        }
        
        // Calculate how many days until the week ends
        const daysUntilWeekEnd = 7 - dayOfWeek;
        
        // Use the event duration directly (it's already calculated correctly)
        // Duration represents the number of days the event spans (e.g., 3 days = Dec 24, 25, 26)
        const eventDuration = event.duration || 1;
        
        // Span should be the minimum of: days until week end, event duration
        // This ensures the event doesn't span beyond its actual end date or beyond the week
        const spanDays = Math.min(daysUntilWeekEnd, eventDuration);
        
        return Math.max(0, spanDays); // Ensure non-negative
    };
    
    // Get color class for event (alternate between blue and purple)
    const getEventColorClass = (index) => {
        return index % 2 === 0 ? 'calendar-event-blue' : 'calendar-event-purple';
    };
    
    // Process events for display
    // Separate single-day and multi-day events
    const singleDayEventsByDate = {};
    const multiDayEvents = [];
    const multiDayEventsByRow = {}; // Track multi-day events per row for stacking
    
    calendarDays.forEach((dayInfo, index) => {
        if (!dayInfo.isCurrentMonth) return;
        
        // Get all events that should be displayed on this date
        const dayEvents = events.filter(event => shouldDisplayEventOnDate(event, dayInfo.date));
        // Convert day of week to Monday-based: Monday = 0, Tuesday = 1, ..., Sunday = 6
        const dayOfWeekJs = dayInfo.date.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
        const dayOfWeek = (dayOfWeekJs + 6) % 7; // Monday = 0, Tuesday = 1, ..., Sunday = 6
        const row = Math.floor(index / 7);
        const dateKey = `${dayInfo.date.getFullYear()}-${String(dayInfo.date.getMonth() + 1).padStart(2, '0')}-${String(dayInfo.date.getDate()).padStart(2, '0')}`;
        
        dayEvents.forEach((event, eventIndex) => {
            const duration = event.duration || 1;
            const isStartDate = isEventStartDate(event, dayInfo.date);
            
            // Skip multi-day events on non-start dates (they're shown as spanning events)
            if (duration > 1 && !isStartDate) {
                return; // Skip this event - it's already shown as a spanning multi-day event
            }
            
            if (duration > 1 && isStartDate) {
                // Multi-day event - only show on start date, span across days
                // Calculate span based on actual event end date, not just week end
                const spanDays = calculateEventSpan(event, dayInfo.date, dayOfWeek);
                
                // Only add if we have at least 1 day to span
                if (spanDays > 0) {
                    // Track event index for this row to stack multiple multi-day events
                    if (!multiDayEventsByRow[row]) {
                        multiDayEventsByRow[row] = [];
                    }

                    // If we've already reached the per-row cap, skip adding this multi-day overlay
                    if (multiDayEventsByRow[row].length >= MAX_VISIBLE_EVENTS_PER_DAY) {
                        return;
                    }
                    
                    // Avoid duplicate multi-day overlays for the same schedule/event in this row
                    const multiKey = event.scheduleId || event.eventId;
                    if (multiDayEventsByRow[row].includes(multiKey)) {
                        return;
                    }

                    const rowEventIndex = multiDayEventsByRow[row].length;
                    multiDayEventsByRow[row].push(multiKey);
                    
                    multiDayEvents.push({
                        event,
                        row,
                        column: dayOfWeek,
                        spanDays,
                        eventIndex: rowEventIndex, // Use row-based index for stacking
                        dayIndex: index
                    });
                }
            } else if (duration === 1) {
                // Single-day event - show in cell
                if (!singleDayEventsByDate[dateKey]) {
                    singleDayEventsByDate[dateKey] = [];
                }
                singleDayEventsByDate[dateKey].push({ event, eventIndex });
            }
        });
    });
    
    // Handle previous month navigation
    const handlePrevMonth = () => {
        if (onMonthChange) {
            const newMonth = month === 0 ? 11 : month - 1;
            const newYear = month === 0 ? year - 1 : year;
            onMonthChange(newMonth, newYear);
        }
    };
    
    // Handle next month navigation
    const handleNextMonth = () => {
        if (onMonthChange) {
            const newMonth = month === 11 ? 0 : month + 1;
            const newYear = month === 11 ? year + 1 : year;
            onMonthChange(newMonth, newYear);
        }
    };

    return (
            <div
                className="calendar-view"
                onClick={() => {
                    setEligibilityPopup({
                        show: false,
                        event: null,
                        position: { top: 0, left: 0 },
                        anchor: 'center',
                        arrow: '50%'
                    });

                    setInfoPopup({
                        show: false,
                        event: null,
                        position: { top: 0, left: 0 },
                        anchor: 'center',
                        arrow: '50%'
                    });
                }}
            >
            {/* Calendar Header */}
            <div className="calendar-header">
                <h2 className="calendar-month-year">
                    {monthNames[month]} {year}
                </h2>

                {/* Navigation box: gray background, no border */}
                <div className="calendar-nav-box">
                    <button
                        className="calendar-nav-button calendar-nav-prev"
                        onClick={handlePrevMonth}
                        aria-label="Previous month"
                    >
                        <i className="fas fa-chevron-left"></i>
                    </button>

                    <button
                        className="calendar-nav-button calendar-nav-next"
                        onClick={handleNextMonth}
                        aria-label="Next month"
                    >
                        <i className="fas fa-chevron-right"></i>
                    </button>
                </div>
            </div>

            {/* Calendar Grid */}
            <div className="calendar-grid">
                {/* Day Headers */}
                <div className="calendar-day-headers">
                    {dayNames.map((dayName, index) => (
                        <div key={index} className="calendar-day-header">
                            {dayName}
                        </div>
                    ))}
                </div>
                
                {/* Calendar Days */}
                <div
                    className="calendar-days"
                    ref={calendarDaysRef}
                    onMouseMoveCapture={(e) => {
                        // Detect underlying info icons when overlays block pointer events
                        try {
                            const x = e.clientX;
                            const y = e.clientY;
                            if (typeof document.elementsFromPoint === 'function') {
                                const stacked = document.elementsFromPoint(x, y);
                                if (stacked && stacked.length > 0) {
                                    const iconEl = stacked.find(el => el.classList && el.classList.contains('calendar-event-info'));
                                    if (iconEl) {
                                        const evtEl = iconEl.closest('.calendar-event') || iconEl.closest('.calendar-event-multiday');
                                        let dataId = evtEl && evtEl.dataset ? evtEl.dataset.eventid : undefined;
                                        let evtObj = null;
                                        if (dataId) {
                                            evtObj = events.find(ev => String(ev.eventId || ev.scheduleId || ev.name) === String(dataId));
                                        }
                                        if (!evtObj && evtEl) {
                                            const nameText = evtEl.textContent && evtEl.textContent.trim();
                                            if (nameText) {
                                                evtObj = events.find(ev => (ev.name || '').trim() === nameText || (nameText.indexOf(ev.name || '') !== -1));
                                            }
                                        }

                                        if (evtObj) {
                                            // Only open if not already showing for the same event
                                            if (!infoPopup.show || String(infoPopup.event?.eventId) !== String(evtObj.eventId)) {
                                                handleInfoHover({ currentTarget: iconEl, preventDefault: () => {}, stopPropagation: () => {} }, evtObj);
                                            }
                                            return;
                                        }
                                    }
                                }
                            }
                        } catch (err) {
                            // ignore
                        }

                        // If we reach here, no info icon under pointer — hide popup if open
                        if (infoPopup.show) handleInfoLeave();
                    }}
                    onClick={(e) => {
                        
                        // If child already handled the click, skip
                        if (e.defaultPrevented) return;

                        // If clicked inside a single-day event (non-multiday), map DOM -> event and open modal
                        const evtEl = e.target && e.target.closest ? e.target.closest('.calendar-event') : null;
                        if (evtEl && !evtEl.classList.contains('calendar-event-multiday')) {
                            const dataId = evtEl.dataset ? evtEl.dataset.eventid : undefined;
                            let evtObj = null;
                            if (dataId) {
                                evtObj = events.find(ev => String(ev.eventId || ev.scheduleId || ev.name) === String(dataId));
                            }
                            if (!evtObj) {
                                const nameText = evtEl.textContent && evtEl.textContent.trim();
                                if (nameText) {
                                    evtObj = events.find(ev => (ev.name || '').trim() === nameText || (nameText.indexOf(ev.name || '') !== -1));
                                }
                            }

                            if (evtObj) {
                                const dayEl = evtEl.closest && evtEl.closest('.calendar-day');
                                let startDateOverride = undefined;
                                if (dayEl && dayEl.dataset && dayEl.dataset.datekey) {
                                    const parts = dayEl.dataset.datekey.split('-');
                                    if (parts.length === 3) {
                                        const y = parseInt(parts[0], 10);
                                        const m = parseInt(parts[1], 10) - 1;
                                        const d = parseInt(parts[2], 10);
                                        startDateOverride = new Date(y, m, d);
                                    }
                                }
                                handleEventClick(evtObj, e, startDateOverride);
                            }
                        }
                    }}
                    onClickCapture={(e) => {
                        // If click is directly on an icon, let its handler run
                        if (e.target.closest('.calendar-event-info')) {
                            return;
                        }

                        // If clicked inside a single-day card or the +more button, let normal handlers run
                        if (e.target.closest('.calendar-event') && !e.target.closest('.calendar-event-multiday')) return;
                        if (e.target.closest('.calendar-event-more')) return;

                        const x = e.clientX;
                        const y = e.clientY;

                        // First, check stacked elements at the point for any info/eligibility icon
                        // This lets icons that are underneath overlapping overlays still receive clicks.
                        if (typeof document.elementsFromPoint === 'function') {
                            const stacked = document.elementsFromPoint(x, y);
                            if (stacked && stacked.length > 0) {
                                const iconEl = stacked.find(el => el.classList && el.classList.contains('calendar-event-info'));
                                if (iconEl) {
                                    // Dispatch native click on the icon element so React handlers run
                                    iconEl.click();
                                    e.preventDefault();
                                    e.stopPropagation();
                                    return;
                                }
                            }
                        }

                        // Find overlays under the click point
                        const hits = [];
                        Object.entries(overlaysRef.current).forEach(([id, el]) => {
                            if (!el) return;
                            const rect = el.getBoundingClientRect();
                            if (x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom) {
                                hits.push({ id, el, z: parseInt(window.getComputedStyle(el).zIndex) || 0 });
                            }
                        });

                        if (hits.length > 0) {
                            // choose topmost by z-index (and DOM order if equal)
                            hits.sort((a, b) => b.z - a.z);
                            const chosen = hits[0];
                            const eventId = chosen.id;
                            // Find event by id - check both eventId and scheduleId
                            const evt = events.find(ev => {
                                const evId = String(ev.eventId || ev.scheduleId || '');
                                const schedId = String(ev.scheduleId || ev.eventId || '');
                                return evId === String(eventId) || schedId === String(eventId);
                            });
                            if (evt) {
                                // Prefer a day index embedded on the overlay element so we can
                                // show the correct occurrence start date for repeated schedules
                                const dayIndexAttr = chosen.el && chosen.el.dataset ? chosen.el.dataset.dayindex : undefined;
                                if (dayIndexAttr !== undefined && dayIndexAttr !== '') {
                                    const di = parseInt(dayIndexAttr, 10);
                                    if (!Number.isNaN(di) && calendarDays[di]) {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        handleEventClick(evt, e, calendarDays[di].date);
                                    } else {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        handleEventClick(evt, e);
                                    }
                                } else {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleEventClick(evt, e);
                                }

                                // bring this overlay to top while active
                                setActiveOverlayId(eventId);
                                setTimeout(() => setActiveOverlayId(null), 3000);
                            }
                        }
                    }}>
                    {calendarDays.map((dayInfo, index) => {
                        const isToday = dayInfo.isCurrentMonth && 
                                       dayInfo.date.toDateString() === new Date().toDateString();
                        
                        const dateKey = `${dayInfo.date.getFullYear()}-${String(dayInfo.date.getMonth() + 1).padStart(2, '0')}-${String(dayInfo.date.getDate()).padStart(2, '0')}`;
                        const row = Math.floor(index / 7);
                        // Calculate dayOfWeek early so we can compute overlays covering this cell
                        const dayOfWeekJsLocal = dayInfo.date.getDay(); // 0 = Sunday
                        const dayOfWeek = (dayOfWeekJsLocal + 6) % 7; // Monday=0..Sunday=6

                        // Calculate how many multi-day overlays cover this specific cell (they consume visible slots)
                        const overlayCountForCell = multiDayEvents.filter(m => m.row === row && (dayOfWeek >= m.column && dayOfWeek < (m.column + m.spanDays))).length;
                        const hasMultiDayInRow = overlayCountForCell > 0;
                        
                        // All events that appear on this calendar day (single-day + multi-day)
                        const allEventsForDateForCountRaw = dayInfo.isCurrentMonth
                            ? events.filter(event => shouldDisplayEventOnDate(event, dayInfo.date))
                            : [];
                        // Deduplicate so multi-day events or repeated schedules are only counted once,
                        // matching what is shown inside the "+x more" modal.
                        const allEventsForDateForCount = deduplicateEvents(allEventsForDateForCountRaw);
                        
                        // Only single-day events are rendered as cards inside the cell;
                        // multi-day events are rendered as spanning overlays above the row.
                        const singleDayEventsForCell = dayInfo.isCurrentMonth
                            ? allEventsForDateForCount.filter(event => {
                                const duration = event.duration || 1;
                                // Only render single-day events (duration === 1) as cards inside the cell.
                                // Multi-day events (duration > 1) are ONLY shown as spanning overlays, never as single-day cards.
                                return duration === 1;
                            })
                            : [];
                        
                        // Show up to the allowed number of single-day events inside the cell;
                        // the rest (and any multi-day events) will be counted into "+x more".
                        // (dayOfWeek and overlayCountForCell already computed above)
                        const allowedSingleDaySlots = Math.max(0, MAX_VISIBLE_EVENTS_PER_DAY - overlayCountForCell);
                        const eventsToShow = singleDayEventsForCell.slice(0, allowedSingleDaySlots);
                        
                        // "+x more" should be based on a simple rule:
                        // If a date has N distinct events (single-day + multi-day),
                        // and the box can effectively show up to MAX_VISIBLE_EVENTS_PER_DAY events
                        // (including extended/multi-day), then we display "+(N - MAX_VISIBLE_EVENTS_PER_DAY) more" when N > MAX_VISIBLE_EVENTS_PER_DAY.
                        // NOTE:
                        // - We render up to MAX_VISIBLE_EVENTS_PER_DAY single-day cards (subject to overlay occupancy),
                        //   while multi-day events are displayed as row overlays.
                        // - From the user's perspective, overlays count towards the visible item limit.
                        const totalEventsCount = allEventsForDateForCount.length; // All distinct events (single-day + multi-day)
                        const visibleEventsCountForBox = Math.min(MAX_VISIBLE_EVENTS_PER_DAY, totalEventsCount);
                        
                        // Remaining events are those beyond what the box can visually accommodate (4)
                        const remainingEventsCount = Math.max(0, totalEventsCount - visibleEventsCountForBox);
                        
                        return (
                            <div
                                key={index}
                                data-datekey={dateKey}
                                className={`calendar-day ${!dayInfo.isCurrentMonth ? 'calendar-day-other-month' : ''} ${isToday ? 'calendar-day-today' : ''}`}
                            >
                                <div className="calendar-day-number">
                                    {dayInfo.day}
                                </div>
                                <div
                                    className="calendar-day-events"
                                    style={(() => {
                                        if (!hasMultiDayInRow) return undefined;
                                        // Match the overlay stacking math from above: EVENT_OFFSET + EVENT_HEIGHT per overlay
                                        const EVENT_OFFSET = 3;
                                        const EVENT_HEIGHT = 22;

                                        // Only reserve space for overlay strip(s) + small offset
                                        const topPad = EVENT_OFFSET + (overlayCountForCell * EVENT_HEIGHT);
                                        return { paddingTop: `${topPad}px` };
                                    })()}
                                >
                                    {eventsToShow.map((event, eventIndex) => (
                                        <div
                                            key={`${event.eventId || event.name || 'event'}-${eventIndex}`}
                                            data-eventid={String(event.eventId || event.scheduleId || event.name)}
                                            className={`calendar-event calendar-event--${event.categoryColor}`}
                                            onClick={(e) => handleEventClick(event, e, dayInfo.date)}
                                        >
                                            <div className="calendar-event-name">
                                                {event.name}
                                                <span
                                                    className="calendar-event-info calendar-event-info--i"
                                                    title="Event info"
                                                    onMouseEnter={(e) => handleInfoHover(e, event)}
                                                    onMouseLeave={handleInfoLeave}
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        if (isMobileView()) openMobileModal('info', event);
                                                    }}
                                                >
                                                    i
                                                </span>
                                                {/* <span
                                                   className="calendar-event-info calendar-event-info--e"
                                                    onMouseEnter={(e) => handleEligibilityHover(e, event)}
                                                    onMouseLeave={handleInfoLeave}
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        if (isMobileView()) openMobileModal('eligibility', event);
                                                    }}
                                                > E </span> */}
                                            </div>
                                            <div className="calendar-event-location">
                                                <i className="fas fa-map-marker-alt" 
                                                    onMouseEnter={(e) =>
                                                    {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        handleLocationHoverIn(event, e, dayInfo.date)
                                                    }}
                                                    onMouseDown={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                    }}
                                                     onClick={(e) => {
                                                        // On mobile open modal with address; on desktop do nothing (hover handles popup)
                                                        if (isMobileView()) {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                            openMobileModal('location', event);
                                                        }
                                                    }}
                                                    > 
                                                </i>
                                                <span className="calendar-event-location-text">{event.location || event.cityName}</span>
                                            </div>
                                            <div className="calendar-event-language">{event.language}</div>
                                        </div>
                                    ))}
                                </div>
                                {remainingEventsCount > 0 && (
                                    <div
                                        className="calendar-event-more"
                                        onClick={(e) => handleMoreEventsClick(dayInfo.date, allEventsForDateForCount, e)}
                                    >
                                        +{remainingEventsCount} {isMarathi ? 'अधिक' : 'more'}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                    
                    {/* Multi-day events overlay */}
                    {multiDayEvents.map(({ event, row, column, spanDays, eventIndex, dayIndex }) => {
                        // Calculate position and width for multi-day events
                        // Each cell is 1/7 of the grid width
                        // Grid has 1px gap between cells, so we need to account for gaps
                        const GRID_GAP = 1; // Grid gap between cells (1px)
                        const cellWidthPercent = 100 / 7;
                        const leftPercent = column * cellWidthPercent;
                        const widthPercent = spanDays * cellWidthPercent;
                        
                        // Calculate gaps: (spanDays - 1) gaps between the cells we're spanning
                        const gapsWidth = (spanDays - 1) * GRID_GAP;
                        
                        // Calculate top position using measured cell dimensions
                        const EVENT_OFFSET = 3; // Small offset from day number
                        const EVENT_HEIGHT = 22; // Height per event for stacking
                        
                        // Use measured cell height and day number height (responsive)
                        const measuredCellHeight = cellHeight || 100; // Fallback to 100px if not measured yet
                        const measuredDayNumberHeight = dayNumberHeight || 25; // Fallback to 25px if not measured yet
                        
                        // Account for grid gaps between rows: row * (measuredCellHeight + GRID_GAP)
                        const topOffset = (row * (measuredCellHeight + GRID_GAP)) + measuredDayNumberHeight + EVENT_OFFSET + (eventIndex * EVENT_HEIGHT);
                        
                        // Calculate max height to ensure event doesn't exceed row boundary
                        // Row starts at: row * (measuredCellHeight + GRID_GAP)
                        // Row ends at: (row + 1) * (measuredCellHeight + GRID_GAP) - GRID_GAP
                        const rowStart = row * (measuredCellHeight + GRID_GAP);
                        const rowEnd = (row + 1) * (measuredCellHeight + GRID_GAP) - GRID_GAP;
                        const availableHeight = rowEnd - topOffset;
                        // Ensure minimum height, but don't exceed row boundary
                        const maxHeight = Math.max(22, availableHeight - 3); // 3px padding from bottom
                        
                        // compute overlay z-index so later overlays are above earlier ones
                        const baseZ = 5; // base under single-day events
                        const overlayZ = baseZ + eventIndex;
                        const isActive = String(activeOverlayId) === String(event.eventId || event.scheduleId);
                        const finalZ = isActive ? 30 : overlayZ;
                        const eventStartDate = getEventStartDate(event);
                        const overlayDate = calendarDays[dayIndex]?.date;
                        const isStartDay =
                            eventStartDate &&
                            overlayDate &&
                            overlayDate.toDateString() === eventStartDate.toDateString();

                        return (
                            <div
                                key={`multiday-${event.eventId}-${dayIndex}-${eventIndex}`}
                                ref={(el) => {
                                    // Store reference using primary identifier (scheduleId or eventId)
                                    const key = String(event.scheduleId || event.eventId || `${dayIndex}-${eventIndex}`);
                                    if (el) {
                                        overlaysRef.current[key] = el;
                                        // Also store with alternate key for better matching
                                        if (event.eventId && event.scheduleId) {
                                            const altKey = String(event.eventId);
                                            overlaysRef.current[altKey] = el;
                                        }
                                    } else {
                                        delete overlaysRef.current[key];
                                        if (event.eventId && event.scheduleId) {
                                            delete overlaysRef.current[String(event.eventId)];
                                        }
                                    }
                                }}
                                data-eventid={String(event.scheduleId || event.eventId || `${dayIndex}-${eventIndex}`)}
                                data-dayindex={String(dayIndex)}
                                className={`calendar-event calendar-event-multiday calendar-event--${event.categoryColor}`}
                                style={{
                                    position: 'absolute',
                                    left: `calc(${leftPercent}% + 0.25rem)`,
                                    width: `calc(${widthPercent}% - 0.5rem - ${gapsWidth}px)`, // Account for grid gaps and padding
                                    top: `${topOffset}px`,
                                    maxHeight: `${maxHeight}px`,
                                    minHeight: '22px',
                                    height: 'auto',
                                    zIndex: finalZ,
                                    overflow: 'hidden',
                                    // Make the overlay container ignore pointer events so it doesn't block
                                    // clicks for underlying elements; the inner content will handle clicks.
                                    pointerEvents: 'none'
                                }}
                            >
                                <div
                                    className="calendar-event-multiday-inner"
                                    style={{ pointerEvents: 'auto' }}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        handleEventClick(event, e, calendarDays[dayIndex].date);
                                    }}
                                    onMouseDown={(e) => {
                                        const key = String(event.scheduleId || event.eventId || `${dayIndex}-${eventIndex}`);
                                        setActiveOverlayId(key);
                                    }}
                                    onMouseUp={() => setActiveOverlayId(null)}
                                >
                                    <div className="calendar-event-name">
                                        {event.name}
                                        <span
                                            className="calendar-event-info calendar-event-info--i"
                                            title="Event info"
                                            onMouseEnter={(e) => handleInfoHover(e, event)}
                                            onMouseLeave={handleInfoLeave}
                                            onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                if (isMobileView()) openMobileModal('info', event);
                                            }}
                                        >
                                            i
                                        </span>
                                        {/* <span
                                            className="calendar-event-info calendar-event-info--e"
                                            onMouseEnter={(e) => handleEligibilityHover(e, event)}
                                            onMouseLeave={handleInfoLeave}
                                            onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                if (isMobileView()) openMobileModal('eligibility', event);
                                            }}
                                        > E </span> */}
                                    </div>
                                    <div className="calendar-event-location">
                                        <i className="fas fa-map-marker-alt"  
                                           onMouseEnter={(e) =>{
                                                e.preventDefault();
                                                e.stopPropagation();
                                                handleLocationHoverIn(event, e, calendarDays[dayIndex].date)
                                            }}
                                             onMouseDown={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                            }}
                                            onClick={(e) => {
                                                if (isMobileView()) {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    openMobileModal('location', event);
                                                }
                                            }}>
                                        </i>
                                        <span className="calendar-event-location-text">{event.location}</span>
                                    </div>
                                    <div className="calendar-event-language">{event.language}</div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
            
            {/* More Events Modal (for "+x more" click) */}
            {isMoreEventsModalOpen && moreEventsDate && (
                <div className="calendar-more-events-overlay" onClick={handleCloseMoreEventsModal}>
                    <div
                        className="calendar-more-events-modal"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="calendar-more-events-header">
                            <h3 className="calendar-more-events-title">
                                {moreEventsDate.toLocaleDateString(undefined, {
                                    weekday: 'long',
                                    year: 'numeric',
                                    month: 'long',
                                    day: 'numeric'
                                })}
                            </h3>
                            <button
                                className="calendar-more-events-close"
                                onClick={handleCloseMoreEventsModal}
                                aria-label="Close"
                            >
                                <i className="fas fa-times"></i>
                            </button>
                        </div>
                        <div className="calendar-more-events-body">
                            {moreEventsList.map((event, index) => (
                                <button
                                    key={`${event.eventId || event.name || 'event'}-more-${index}`}
                                    type="button"
                                    className={`calendar-more-event-item ${getEventColorClass(index)}`}
                                    onClick={(e) => {
                                        // Open single event details for the date shown in the list
                                        handleEventClick(event, e, moreEventsDate);
                                        handleCloseMoreEventsModal();
                                    }}
                                >
                                    <div className="calendar-more-event-name">{event.name}</div>
                                    <div className="calendar-more-event-location">
                                        <i className="fas fa-map-marker-alt"></i>
                                        <span className="calendar-more-event-location-text">
                                            {event.location || event.cityName}
                                        </span>
                                    </div>
                                    {event.language && (
                                        <div className="calendar-more-event-language">
                                            {event.language}
                                        </div>
                                    )}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            )}
            {eligibilityPopup.show && eligibilityPopup.event && (
                <div
                    className={`location-popup popup--${eligibilityPopup.anchor || 'center'} popup--${eligibilityPopup.placement || 'below'}`}
                    style={{
                        position: 'fixed',
                        top: `${eligibilityPopup.position.top}px`,
                        left: `${eligibilityPopup.position.left}px`,
                        ['--popup-arrow']: eligibilityPopup.arrow || '50%'
                    }}
                    onClick={(e) => e.stopPropagation()}
                >
                    <div style={{ marginBottom: '6px' }}>
                        <strong>Eligibility Criteria</strong>
                    </div>

                    <div style={{ fontSize: '0.8rem', lineHeight: '1.4' }}>
                        <Link
                            to={{
                                pathname: `/event_details/${eligibilityPopup.event.eventId}`,
                                state: {
                                    from: 'calendar',
                                    viewMode: 'calendar',
                                    month: currentMonth,
                                    year: currentYear
                                }
                            }}
                        >
                            [Check eligibility]
                        </Link>
                        &nbsp;of the course on detail page.
                    </div>
                </div>
            )}
            {infoPopup.show && infoPopup.event && (
                <div
                    className={`location-popup popup--${infoPopup.anchor || 'center'} popup--${infoPopup.placement || 'below'}`}
                    style={{
                        position: 'fixed',
                        top: `${infoPopup.position.top}px`,
                        left: `${infoPopup.position.left}px`,
                        ['--popup-arrow']: infoPopup.arrow || '50%'
                    }}
                    onClick={(e) => e.stopPropagation()}
                >
                    <strong>Event Information</strong>
                    <div style={{ fontSize: '0.8rem', marginTop: '6px' }}>
                        {infoLoading && 'Loading...'}
                            {!infoLoading &&
                                (getFirstLineOfDescription(
                                    eventDetailsCache[infoPopup.event.eventId]
                                ) || 'Event details available on detail page.')
                        }
                    </div>
                </div>
            )}
            {/* Mobile modal overlay for touch devices */}
            {mobileModal.show && mobileModal.event && (
                <div className="mobile-modal-overlay" onClick={() => closeMobileModal()} style={{position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', zIndex:10000, display:'flex', alignItems:'center', justifyContent:'center'}}>
                    <div className="mobile-modal-box" onClick={(e)=>e.stopPropagation()} style={{background:'#fff', padding:16, borderRadius:8, maxWidth:'92%', width:340}}>
                        <div style={{display:'flex', justifyContent:'space-between', alignItems:'center'}}>
                            <strong>{mobileModal.type === 'location' ? 'Address' : mobileModal.type === 'info' ? 'Event Information' : 'Eligibility'}</strong>
                            <button className="btn btn-sm btn-light" onClick={() => closeMobileModal()}>Close</button>
                        </div>
                        <div style={{marginTop:8, fontSize:'0.9rem'}}>
                            {mobileModal.type === 'location' && (
                                <>
                                    <div style={{marginBottom:8, wordBreak:'break-word'}}>{mobileModal.event.fullAddress || mobileModal.event.location || 'Address not available'}</div>
                                    {mobileModal.event.googleMapsUrl ? <a href={mobileModal.event.googleMapsUrl} target="_blank" rel="noopener noreferrer">Open in Google Maps</a> : <div style={{color:'#6c757d', fontStyle:'italic'}}>Google Maps link not available</div>}
                                </>
                            )}
                            {mobileModal.type === 'eligibility' && (
                                <div style={{fontSize:'0.9rem'}}>
                                    <Link to={`/event_details/${mobileModal.event.eventId}`} onClick={() => closeMobileModal()} style={{color:'#007bff', textDecoration:'underline'}}>[Check eligibility]</Link>
                                    &nbsp;on the event detail page.
                                </div>
                            )}
                            {mobileModal.type === 'info' && (
                                <div style={{fontSize:'0.9rem'}}>
                                    {infoLoading && 'Loading...'}
                                    {!infoLoading && (getFirstLineOfDescription(eventDetailsCache[mobileModal.event.eventId]) || 'Event details available on detail page.')}
                                    <div style={{marginTop:8}}>
                                        <Link to={`/event_details/${mobileModal.event.eventId}`} onClick={() => closeMobileModal()} style={{color:'#007bff', textDecoration:'underline'}}>{isMarathi ? ma.home['View full details'] : en.home['View full details']}</Link>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
            
            {/* Event Details Modal */}
            <EventDetailsModal
                event={selectedEvent}
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                onShare={handleShare}
            />
        </div>
    );
};

CalendarView.propTypes = {
    events: PropTypes.arrayOf(PropTypes.shape({
        eventId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        date: PropTypes.string,
        dateKey: PropTypes.string,
        name: PropTypes.string,
        location: PropTypes.string,
        language: PropTypes.string,
        duration: PropTypes.number,
        status: PropTypes.string,
        startDate: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string])
    })),
    currentMonth: PropTypes.number, // 0-11
    currentYear: PropTypes.number,
    onMonthChange: PropTypes.func
};

CalendarView.defaultProps = {
    events: [],
    currentMonth: undefined,
    currentYear: undefined,
    onMonthChange: null
};

export default CalendarView;